/**
 * The incident register.
 *
 * THIS SCREEN IS OPENED IN AN EMERGENCY, by someone who has probably never
 * opened it before, at an hour when they are not at their best. Everything on
 * it follows from that:
 *
 *   * "Declare an incident" is the first thing, in the primary colour, above
 *     the list. Not in a kebab menu, not behind a tab.
 *   * The declaration form asks three questions and says so.
 *   * Every row leads with the most urgent clock, not with a reference number.
 *   * The disclaimer that we do not file on anyone's behalf is on the page
 *     before any incident exists, so nobody meets it for the first time in a
 *     confirmation dialog at hour 23.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DeadlineView, IncidentSeverity } from '@opencomply/shared';
import { INCIDENT_SEVERITIES } from '@opencomply/shared';
import { api, qs, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton, ErrorNotice,
} from '../components/ui';
import { DeadlineChip, useTick } from '../components/IncidentClocks';
import { useIncidentT, type IncidentKey } from '../lib/incidentStrings';
import { cn } from '../lib/cn';

interface IncidentRow {
  id: string;
  reference: string;
  title: string;
  severity: string;
  status: string;
  awareAt: string;
  significant: string;
  deadlines: DeadlineView[];
  hasMissedDeadline: boolean;
}

const SEVERITY_TONE: Record<string, 'good' | 'warning' | 'serious' | 'critical' | 'neutral'> = {
  LOW: 'neutral',
  MEDIUM: 'warning',
  HIGH: 'serious',
  CRITICAL: 'critical',
};

/**
 * A `datetime-local` value for "now", in the BROWSER'S timezone.
 *
 * `toISOString()` would give UTC and silently shift the default by the offset —
 * someone in Athens would see a time two hours behind, accept it, and start
 * every clock two hours early. The offset arithmetic below is the fix.
 */
function localNowValue(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function IncidentsPage() {
  const { tx, txn, formatDateTime } = useIncidentT();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const now = useTick();

  const [status, setStatus] = useState('');
  const [declareOpen, setDeclareOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['incidents', { status }],
    queryFn: () => api.get<{ items: IncidentRow[] }>(`/incidents${qs({ status })}`),
    // The register carries live countdowns, so a stale cache is a wrong clock.
    staleTime: 15_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1">{tx('incident.title')}</h1>
          <p className="oc-muted mt-1 max-w-2xl">{tx('incident.subtitle')}</p>
        </div>
        {allows('incident.manage') && (
          <Button variant="primary" onClick={() => setDeclareOpen(true)}>
            {tx('incident.declare')}
          </Button>
        )}
      </div>

      {/* The disclaimer, before anything else and before any incident exists. */}
      <div className="rounded-xl border border-warning/40 bg-warning/[0.07] p-3 sm:p-4">
        <p className="text-sm text-ink-secondary leading-relaxed">
          <strong className="text-ink">{tx('incident.notFiled.short')}</strong>{' '}
          {tx('incident.notFiled.banner')}
        </p>
      </div>

      <Card>
        <CardHeader
          title={txn('incident.count', items.length)}
          action={
            <select
              className="oc-input oc-input-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label={tx('incident.filter.all')}
            >
              <option value="">{tx('incident.filter.all')}</option>
              {['OPEN', 'CONTAINED', 'RESOLVED', 'CLOSED'].map((s) => (
                <option key={s} value={s}>
                  {tx(`incident.status.${s}` as IncidentKey)}
                </option>
              ))}
            </select>
          }
        />

        {isLoading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        )}

        {error && (
          <ErrorNotice
            message={tx('incident.loadFailed')}
            onRetry={() => void refetch()}
            retryLabel={tx('incident.retry')}
          />
        )}

        {!isLoading && !error && items.length === 0 && (
          <EmptyState
            title={tx('incident.empty.title')}
            description={tx('incident.empty.body')}
            action={
              allows('incident.manage') ? (
                <Button variant="primary" onClick={() => setDeclareOpen(true)}>
                  {tx('incident.declare')}
                </Button>
              ) : undefined
            }
          />
        )}

        <div className="space-y-2">
          {items.map((incident) => (
            <button
              key={incident.id}
              onClick={() => navigate(`/incidents/${incident.id}`)}
              className={cn(
                'w-full text-left rounded-xl border p-3 sm:p-4 transition-colors',
                'hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                incident.hasMissedDeadline
                  ? 'border-critical/40 border-l-4 border-l-critical bg-critical/[0.04]'
                  : 'border-line',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-ink-muted">{incident.reference}</span>
                    <Pill tone={SEVERITY_TONE[incident.severity] ?? 'neutral'}>
                      {tx(`incident.severity.${incident.severity}` as IncidentKey)}
                    </Pill>
                    <Pill tone={incident.status === 'CLOSED' ? 'good' : 'neutral'}>
                      {tx(`incident.status.${incident.status}` as IncidentKey)}
                    </Pill>
                    {/* Said in words, not only in the red border.
                        `DeadlineChip` shows the next OUTSTANDING clock, so once
                        a missed stage is finally submitted the chip moves on
                        and colour would be the only remaining signal — which
                        is no signal at all to a colour-blind reader, and a
                        missed deadline is the one fact in this row nobody may
                        miss. */}
                    {incident.hasMissedDeadline && (
                      <Pill tone="critical">{tx('incident.missedDeadline')}</Pill>
                    )}
                  </div>
                  <p className="font-medium text-ink mt-1 text-balance">{incident.title}</p>
                  <p className="text-xs text-ink-muted mt-0.5">
                    {tx('incident.clocks.awareAt', { when: formatDateTime(incident.awareAt) })}
                  </p>
                </div>
                <div className="shrink-0">
                  <DeadlineChip deadlines={incident.deadlines} now={now} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </Card>

      <DeclareModal
        open={declareOpen}
        onClose={() => setDeclareOpen(false)}
        onDeclared={(id) => {
          void queryClient.invalidateQueries({ queryKey: ['incidents'] });
          setDeclareOpen(false);
          // Straight into the incident: the next thing they need is the clock.
          navigate(`/incidents/${id}`);
        }}
      />
    </div>
  );
}

/**
 * Declaration.
 *
 * THREE FIELDS. The description is there and optional, and the form says so,
 * because the one thing that must not happen is a responder abandoning this
 * form because it asked for things they do not know yet.
 */
function DeclareModal({
  open,
  onClose,
  onDeclared,
}: {
  open: boolean;
  onClose: () => void;
  onDeclared: (id: string) => void;
}) {
  const { tx } = useIncidentT();
  const { t } = useTranslation();
  const [awareAt, setAwareAt] = useState(localNowValue);
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('HIGH');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/incidents', {
        // `datetime-local` has no timezone, so the browser's own interpretation
        // is the right one: the responder typed a local wall-clock time.
        awareAt: new Date(awareAt).toISOString(),
        title: title.trim(),
        severity,
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (incident) => {
      setTitle('');
      setDescription('');
      setError(null);
      onDeclared(incident.id);
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN_ERROR';
      setError(
        code === 'INCIDENT_AWARE_AT_IN_FUTURE'
          ? tx('incident.declare.futureError')
          : tx(`incident.error.${code}` as IncidentKey, { defaultValue: code }),
      );
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={tx('incident.declare.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!title.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {tx('incident.declare.submit')}
          </Button>
        </>
      }
    >
      <p className="oc-muted mb-4">{tx('incident.declare.intro')}</p>

      <Field
        label={tx('incident.declare.awareAt')}
        hint={tx('incident.declare.awareAtHint')}
        required
        htmlFor="incident-aware-at"
      >
        <div className="flex gap-2">
          <input
            id="incident-aware-at"
            type="datetime-local"
            className="oc-input flex-1"
            value={awareAt}
            max={localNowValue()}
            onChange={(e) => setAwareAt(e.target.value)}
          />
          <Button onClick={() => setAwareAt(localNowValue())}>
            {tx('incident.declare.awareAtNow')}
          </Button>
        </div>
      </Field>

      <Field
        label={tx('incident.declare.title_field')}
        hint={tx('incident.declare.titleHint')}
        required
        htmlFor="incident-title"
      >
        <input
          id="incident-title"
          className="oc-input"
          value={title}
          maxLength={500}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>

      <Field
        label={tx('incident.declare.severity')}
        hint={tx('incident.declare.severityHint')}
        required
        htmlFor="incident-severity"
      >
        <select
          id="incident-severity"
          className="oc-input"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
        >
          {INCIDENT_SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {tx(`incident.severity.${s}` as IncidentKey)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={tx('incident.declare.description')} htmlFor="incident-description">
        <textarea
          id="incident-description"
          className="oc-input min-h-24"
          value={description}
          maxLength={20_000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      {error && <p role="alert" className="text-sm text-critical">{error}</p>}
    </Modal>
  );
}
